/* eslint-disable no-unused-vars */
import React, { useMemo } from "react";
import { MAX_SESSION_DURATION_HOURS } from "../../DeclarationCourse/SessionList";
import { DV2 } from "../theme";
import { hours } from "../format";
import { FieldLabel, fieldStyle, useDv2Theme } from "../layout/Ui";
import { TimeSlot, clampDuration, timeSlots } from "./planningRules";

interface TimeSelectProps {
    id?: string;
    label?: string;
    /** « HH:mm » ou null. */
    value: string | null;
    onChange: (value: string) => void;
    /** Pastilles des créneaux voisins sous le champ (maquette 1b). */
    chips?: boolean;
    error?: boolean;
    describedBy?: string;
}

/**
 * Heure de début : une liste de créneaux de 15 minutes (recommandation de
 * l'audit : plus de saisie libre qui se corrompt, pas de secondes). Une heure
 * hors du pas (ancienne saisie à 5 minutes) reste proposée.
 */
export const TimeSelect = ({ id, label = "Heure de début", value, onChange, chips = false, error, describedBy }: TimeSelectProps) => {
    const { primary, fontFamily } = useDv2Theme();
    const slots = useMemo<TimeSlot[]>(() => {
        const base = timeSlots();
        if (value && !base.some((s) => s.value === value)) {
            return [...base, { value, label: value.replace(":", "h") }].sort((a, b) => a.value.localeCompare(b.value));
        }
        return base;
    }, [value]);
    const index = value ? slots.findIndex((s) => s.value === value) : -1;
    const neighbours = index >= 0 ? slots.slice(Math.max(0, index - 2), index + 2) : [];

    return (
        <div>
            {label && <FieldLabel htmlFor={id}>{label}</FieldLabel>}
            <select
                id={id}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                aria-invalid={error || undefined}
                aria-describedby={describedBy}
                style={{ ...fieldStyle(error), fontFamily, appearance: "auto" as any }}
            >
                <option value="" disabled>Choisir une heure</option>
                {slots.map((slot) => <option key={slot.value} value={slot.value}>{slot.label}</option>)}
            </select>
            {chips && neighbours.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }} aria-label="Créneaux proches">
                    {neighbours.map((slot) => {
                        const active = slot.value === value;
                        return (
                            <button
                                key={slot.value}
                                type="button"
                                onClick={() => onChange(slot.value)}
                                aria-pressed={active}
                                style={{ fontFamily, fontSize: 12, padding: "5px 10px", borderRadius: 999, border: "none", cursor: "pointer", background: active ? primary : DV2.borderSoft, color: active ? "#fff" : DV2.muted, fontWeight: active ? 800 : 600 }}
                            >
                                {slot.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

interface DurationStepperProps {
    id?: string;
    label?: string;
    /** Heures décimales. */
    value: number;
    onChange: (value: number) => void;
    size?: "md" | "sm";
}

/** Durée : « 1 h 30 » avec − et + par quart d'heure, entre 15 minutes et 4 heures. */
export const DurationStepper = ({ id, label = "Durée", value, onChange, size = "md" }: DurationStepperProps) => {
    const { primary, fontFamily } = useDv2Theme();
    const btn = (glyph: string, delta: number, ariaLabel: string, disabled: boolean) => (
        <button
            type="button"
            aria-label={ariaLabel}
            disabled={disabled}
            onClick={() => onChange(clampDuration(value + delta))}
            style={{ width: size === "sm" ? 28 : 36, height: size === "sm" ? 28 : 36, borderRadius: size === "sm" ? 8 : 10, border: "none", background: DV2.borderSoft, color: disabled ? DV2.border : primary, fontWeight: 800, fontSize: 18, cursor: disabled ? "default" : "pointer", fontFamily }}
        >
            {glyph}
        </button>
    );
    return (
        <div>
            {label && <FieldLabel htmlFor={id}>{label}</FieldLabel>}
            <div id={id} role="group" aria-label={label || "Durée"} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", border: `1px solid ${DV2.border}`, borderRadius: 12, padding: size === "sm" ? "4px 4px 4px 12px" : "8px 8px 8px 14px", background: "#fff", fontFamily }}>
                <span style={{ fontSize: size === "sm" ? 14 : 16, fontWeight: 700 }} aria-live="polite">{hours(value)}</span>
                <div style={{ display: "flex", gap: 6 }}>
                    {btn("−", -0.25, "Réduire la durée de 15 minutes", value <= 0.25)}
                    {btn("+", 0.25, "Augmenter la durée de 15 minutes", value >= MAX_SESSION_DURATION_HOURS)}
                </div>
            </div>
        </div>
    );
};
